import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { ArrowRight, ShoppingCart, Eye, Package, Search, SlidersHorizontal, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import NavBar from '../components/NavBar';
import SmartImage from '../components/SmartImage';

const CategoryProducts = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('products');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Filter & Sort States
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedStock, setSelectedStock] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadData();
  }, [categoryId]);

  useEffect(() => {
    if (currentPage === 'home') {
      navigate('/');
    } else if (currentPage === 'profile') {
      navigate('/profile');
    }
  }, [currentPage, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsSnapshot, categoriesSnapshot] = await Promise.all([
        getDocs(query(collection(db, COLLECTIONS.PRODUCTS), where('categoryId', '==', categoryId))),
        getDocs(collection(db, COLLECTIONS.CATEGORIES))
      ]);

      const productsData = [];
      productsSnapshot.forEach((doc) => {
        productsData.push({ id: doc.id, ...doc.data() });
      });

      let categoryData = null;
      categoriesSnapshot.forEach((doc) => {
        if (doc.id === categoryId) {
          categoryData = { id: doc.id, ...doc.data() };
        }
      });

      setProducts(productsData);
      setCategory(categoryData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
  };

  // Get unique brands from products
  const brands = useMemo(() => {
    const uniqueBrands = [...new Set(products.map(p => p.brand).filter(Boolean))];
    return uniqueBrands.sort();
  }, [products]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Brand filter
    if (selectedBrand !== 'all') {
      filtered = filtered.filter(p => p.brand === selectedBrand);
    }

    // Stock filter
    if (selectedStock !== 'all') {
      filtered = filtered.filter(p => p.stock === selectedStock);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '', 'ar');
        case 'price-low':
          return parseFloat(a.price?.replace(/[^\d.]/g, '') || 0) - parseFloat(b.price?.replace(/[^\d.]/g, '') || 0);
        case 'price-high':
          return parseFloat(b.price?.replace(/[^\d.]/g, '') || 0) - parseFloat(a.price?.replace(/[^\d.]/g, '') || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [products, searchTerm, selectedBrand, selectedStock, sortBy]);

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedBrand('all');
    setSelectedStock('all');
    setSortBy('name');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <NavBar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />
        <div className="pt-24 flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <NavBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <div className="pt-24 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowRight className="w-5 h-5" />
            العودة للرئيسية
          </button>

          {category && (
            <div className={`bg-gradient-to-r from-${category.color}-500 to-${category.color}-600 rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8 text-white`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-xl">
                  <SmartImage
                    src={category.icon}
                    alt={category.name}
                    size="medium"
                    fallback="📦"
                    className="text-white"
                  />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">{category.name}</h1>
                  {category.description && (
                    <p className="text-sm sm:text-base md:text-lg opacity-90 mt-2">{category.description}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm opacity-90">
                <span>إجمالي المنتجات: {products.length}</span>
                <span>•</span>
                <span>النتائج المعروضة: {filteredProducts.length}</span>
              </div>
            </div>
          )}

          {/* Search and Filters */}
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6">
            {/* Search Bar */}
            <div className="relative mb-4">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="ابحث في المنتجات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
              />
            </div>

            {/* Filter Toggle Button - Mobile */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden w-full bg-gray-800 text-white py-3 rounded-lg flex items-center justify-center gap-2 mb-4"
            >
              <SlidersHorizontal className="w-5 h-5" />
              {showFilters ? 'إخفاء الفلاتر' : 'عرض الفلاتر'}
            </button>

            {/* Filters */}
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ${showFilters ? 'block' : 'hidden lg:grid'}`}>
              {/* Brand Filter */}
              {brands.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">العلامة التجارية</label>
                  <select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                  >
                    <option value="all">جميع العلامات</option>
                    {brands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Stock Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">التوفر</label>
                <select
                  value={selectedStock}
                  onChange={(e) => setSelectedStock(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                >
                  <option value="all">الكل</option>
                  <option value="متوفر">متوفر</option>
                  <option value="غير متوفر">غير متوفر</option>
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">ترتيب حسب</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                >
                  <option value="name">الاسم</option>
                  <option value="price-low">السعر: من الأقل للأعلى</option>
                  <option value="price-high">السعر: من الأعلى للأقل</option>
                </select>
              </div>

              {/* Reset Button */}
              <div className="flex items-end">
                <button
                  onClick={resetFilters}
                  className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <X className="w-4 h-4" />
                  إعادة تعيين
                </button>
              </div>
            </div>
          </div>

          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {filteredProducts.map((product) => (
                <div key={product.id} className="bg-white rounded-xl shadow-md hover:shadow-2xl transition-all overflow-hidden group">
                  <div className="p-6">
                    <div className="h-48 w-full flex items-center justify-center mb-4 overflow-hidden rounded-lg">
                      <SmartImage
                        src={product.image}
                        alt={product.name}
                        size="large"
                        fallback="📦"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 text-center h-12 line-clamp-2">{product.name}</h3>
                    <p className="text-3xl font-bold text-yellow-600 text-center mb-4">{product.price}</p>

                    {product.brand && (
                      <p className="text-sm text-gray-600 text-center mb-2">العلامة: {product.brand}</p>
                    )}

                    <p className={`text-sm text-center mb-4 ${product.stock === 'متوفر' ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {product.stock}
                    </p>

                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/product/${product.id}`)}
                        className="flex-1 bg-gray-800 hover:bg-gray-900 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        التفاصيل
                      </button>
                      <button
                        onClick={() => handleAddToCart(product)}
                        className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        أضف
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md p-8 sm:p-12 text-center">
              <Package className="w-16 h-16 sm:w-24 sm:h-24 mx-auto text-gray-300 mb-4" />
              <p className="text-lg sm:text-xl text-gray-600 mb-2">
                {products.length > 0 ? 'لا توجد منتجات تطابق البحث' : 'لا توجد منتجات في هذا التصنيف حالياً'}
              </p>
              {products.length > 0 && (
                <button
                  onClick={resetFilters}
                  className="mt-4 text-yellow-600 hover:text-yellow-700 font-medium"
                >
                  إعادة تعيين الفلاتر
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryProducts;
